/* ============================================================
   Data hooks for the Auto-Dub pages. Each one wraps a react-query
   read and seeds the shared stores the (unchanged) components
   render from: the Recoil atoms, the channel registry behind
   mockData.channel(), and the CREDIT_RATE / CREDIT_BALANCE /
   PLAYLISTS live bindings.
   ============================================================ */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRecoilState } from 'recoil';
import repositories from '../repositories.jsx';
import { autoDubChannelsState, autoDubWorkflowsState } from '../store.jsx';
import { normalizeChannel, normalizeWorkflow } from '../services/autoDub/mappers.js';
import { setChannelRegistry, setCreditConfig, setPlaylists } from '../services/autoDub/mockData.js';
import autoDubService from '../services/autoDub/autoDubService.js';

/* Connected channels — same ['channels'] cache ChannelsPage uses, so an
   OAuth invalidation there refreshes the builder too. */
export function useAutoDubChannels() {
	const [channels, setChannels] = useRecoilState(autoDubChannelsState);
	const query = useQuery({
		queryKey: ['channels'],
		queryFn: () => repositories.channel.list(),
	});

	// Every channel, reauth-required ones included — used to resolve channels
	// already referenced by existing workflows (avatars, edit hydration).
	const allChannels = useMemo(() => (query.data ?? []).map(normalizeChannel), [query.data]);

	useEffect(() => {
		if (!query.data) return;
		// Registry keeps every channel so avatars in existing workflows still
		// resolve when a channel needs reauth (it shows a warning badge instead
		// of disappearing). The picker list stays connected-only so broken
		// channels can't be chosen for new workflows.
		setChannelRegistry(allChannels);
		setChannels(allChannels.filter(c => c.connected));
	}, [query.data, allChannels, setChannels]);

	return { channels, allChannels, ready: query.isFetched };
}

export function useAutoDubWorkflows() {
	const [workflows, setWorkflows] = useRecoilState(autoDubWorkflowsState);
	const query = useQuery({
		queryKey: ['autoDubWorkflows'],
		queryFn: () => repositories.autoDub.workflows.list(),
	});

	useEffect(() => {
		if (!query.data) return;
		setWorkflows((query.data ?? []).map(normalizeWorkflow));
	}, [query.data, setWorkflows]);

	return { workflows, ready: query.isFetched };
}

/* Billing facts for the cost preview — feeds CREDIT_RATE / CREDIT_BALANCE. */
export function useAutoDubConfig() {
	const query = useQuery({
		queryKey: ['autoDubConfig'],
		queryFn: () => repositories.autoDub.config(),
		staleTime: 5 * 60 * 1000,
	});

	useEffect(() => {
		if (!query.data) return;
		setCreditConfig({
			rate: query.data.credit_rate_per_second_per_language,
			balance: query.data.credit_balance,
		});
	}, [query.data]);

	return query;
}

/* The source channel's playlist titles — feeds the PLAYLISTS live binding
   StepVideos renders its filter pills from. */
export function useSourcePlaylists(sourceId) {
	const query = useQuery({
		queryKey: ['autoDubPlaylists', sourceId],
		queryFn: () => autoDubService.listPlaylists(sourceId),
		enabled: !!sourceId,
	});

	useEffect(() => {
		if (!query.data) return;
		setPlaylists(query.data);
	}, [query.data]);

	return query;
}
